import React from 'react'
import { useTranslation } from 'react-i18next'
import { Pressable, View } from 'react-native'
import Animated, {
  interpolate,
  interpolateColor,
  useAnimatedStyle,
  useDerivedValue,
  withTiming,
} from 'react-native-reanimated'
import cn from '@ui/utils/cn'
import useAnimatedPress from '@ui/animated/useAnimatedPress'
import useRWD from '@ui/theme/useRWD'
import { gap } from '@ui/theme/values'
import Chip from '@ui/elements/Chip/Chip'
import Dropdown from '@ui/elements/Dropdown'
import Status from '@ui/elements/Status'
import Checkbox from '@ui/forms/Checkbox'
import Typography from '@ui/typography'
import { Image } from 'expo-image'

import Highlights from '@ui/effects/Highlights'
import { FrameTileProps as Props } from './types'
import {
  colorActive,
  colorInactive,
  colorDetailBorder,
  colorDetailText,
  getHeight,
  styleImage,
  variantsMap,
} from './styles'

function isReactNode(value: any): value is React.ReactNode {
  return (
    value === null ||
    value === undefined ||
    typeof value === 'string' ||
    typeof value === 'number' ||
    typeof value === 'boolean' ||
    React.isValidElement(value) ||
    (Array.isArray(value) && value.every(isReactNode))
  )
}

export default function FrameTile(props: Props) {
  const {
    title,
    type,
    checked,
    image,
    details,
    extras,
    variant = 'default',
    badge,
    dropdown,
    onPress,
  } = props

  const {
    animation: animationPress,
    onPressIn,
    onPressOut,
  } = useAnimatedPress()
  const { screen } = useRWD()
  const { t } = useTranslation(['common'])
  const isDisabled = variant === 'disabled'

  const isCheckable = checked != null
  const height = getHeight(isCheckable)
  const borderRadius = isCheckable ? 32 : 24

  const styleContainer = {
    borderRadius,
    minWidth: screen.width - 2 * gap,
  }

  const variantValue = useDerivedValue(
    () => withTiming(variantsMap.indexOf(variant), { duration: 300 }),
    [variant]
  )

  const animatedTextColor = useAnimatedStyle(() => {
    const color = interpolateColor(
      variantValue.value,
      variantsMap.map((v) => {
        'worklet'
        return variantsMap.indexOf(v)
      }),
      variantsMap.map((v) => {
        'worklet'
        return colorActive[v]
      })
    )
    return {
      color,
    }
  })

  const animatedDetailContainer = useAnimatedStyle(() => {
    const border = colorDetailBorder
    return {
      borderColor: interpolateColor(
        variantValue.value,
        variantsMap.map((v) => {
          'worklet'
          return variantsMap.indexOf(v)
        }),
        variantsMap.map((v) => {
          'worklet'
          return border[v]
        })
      ),
    }
  })

  const animatedDetailText = useAnimatedStyle(() => {
    const text = colorDetailText
    return {
      color: interpolateColor(
        variantValue.value,
        variantsMap.map((v) => {
          'worklet'
          return variantsMap.indexOf(v)
        }),
        variantsMap.map((v) => {
          'worklet'
          return text[v]
        })
      ),
    }
  })

  const animatedImage = useAnimatedStyle(() => ({
    transform: [
      {
        scale: interpolate(animationPress.value, [0, 1], [1, 1.1]),
      },
    ],
  }))

  const Container = !onPress ? View : Pressable

  return (
    <Container
      onPressIn={onPressIn}
      onPressOut={onPressOut}
      style={styleContainer}
      disabled={isDisabled}
      onPress={onPress}
      className="relative inset-0 flex h-max flex-1 flex-col"
    >
      <Highlights
        variant={variant}
        animationPress={animationPress}
        borderWidths={{
          borderWidth: variant === 'active' ? 2 : 1,
        }}
        borderRadiuses={{
          borderRadius,
        }}
      />
      <View
        style={[styleContainer, { height }]}
        className="relative inset-0 flex flex-row items-center justify-between pl-12 pr-16"
      >
        <View className="flex flex-row items-center gap-12">
          {isCheckable && (
            <Checkbox
              value={checked}
              colorActive={colorActive[variant]}
              colorInactive={colorInactive[variant]}
              noninteractive
            />
          )}
          <View className="flex flex-col gap-4">
            <View className="flex flex-row items-center gap-4">
              <Typography variant="MDB" style={animatedTextColor}>
                {title}
              </Typography>
              {type && (
                <Typography
                  variant="MD"
                  style={animatedTextColor}
                  className={cn('opacity-50')}
                >
                  {t(`common:${type}`)}
                </Typography>
              )}
              {badge && <Status text={badge.text} variant={badge.variant} />}
            </View>
            <View className="flex flex-row items-center gap-2">
              {details
                ?.filter((detail) => !!detail)
                .map((detail, index) => (
                  <Chip
                    key={index}
                    label={typeof detail === 'string' ? detail : detail[0]}
                    value={typeof detail === 'string' ? undefined : detail[1]}
                    styleContainer={animatedDetailContainer}
                    styleText={animatedDetailText}
                  />
                ))}
            </View>
          </View>
        </View>
        {!!image && (
          <Animated.View
            className={cn('h-full justify-center', isDisabled && 'opacity-25')}
            style={[styleImage, animatedImage]}
          >
            <Image
              transition={200}
              contentFit="contain"
              source={image}
              style={styleImage}
            />
          </Animated.View>
        )}
      </View>
      {extras && (
        <View
          className="flex flex-1 flex-row items-center gap-2 border-t border-black/10 py-8 pl-12"
          style={{ height: 96 }}
        >
          {extras
            ?.filter(([_, value]) => !!value)
            .map(([label, value], index) => (
              <Chip
                key={index}
                label={label}
                value={value}
                styleContainer={animatedDetailContainer}
                styleText={animatedDetailText}
              />
            ))}
        </View>
      )}
      {isReactNode(dropdown) ? dropdown : <Dropdown {...dropdown} />}
    </Container>
  )
}
